import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Table, Input, Modal, notification, Form, Switch } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    CheckCircleOutlined,
    LoadingOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';

import Counterparty from '@components/users/counterparty';
import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';

import { exception, securityRestriction } from '@extensions/notification';
import { delayAction } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { serverFetch } from '@flc/server';

import { ICounterparty } from '@entities/counterparty';
import { ICounterpartyFilter } from '@entities/counterparty-filter';
import { IUserSession } from '@entities/user-session';

import { Permission, hasPermission } from '@enums/permission';

const filterContext: string = 'Counterparties';

const Counterparties = () => {
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const [modal, modalContextHolder] = Modal.useModal();

    const initFilter: ICounterpartyFilter = {};

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();
    const [api, contextHolder] = notification.useNotification();

    const [counterparties, setCounterparties] = useState<Array<ICounterparty>>([]);
    const [currentCounterparty, setCurrentCounterparty] = useState<ICounterparty>();

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);

    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [counterpartyOpen, setCounterpartyOpen] = useState<boolean>(false);

    const filter = useAppSelector<ICounterpartyFilter>((s) => s.filters[filterContext]);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            setCounterparties([]);
            setSelectedIds([]);
            setCurrentCounterparty(undefined);

            const promises = [
                await serverFetch('counterparties', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения контрагентов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCounterparties(result[0][0]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDeleteCounterparty = () => {
        serverFetch(`counterparties/${currentCounterparty?.id}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления контрагента', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        disabled: loading,
                        key: 'refresh',
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentCounterparty,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCounterparties)) {
                                securityRestriction(api, [Permission.ManageCounterparties]);
                                return;
                            }

                            setCounterpartyOpen(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentCounterparty,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCounterparty)) {
                                securityRestriction(api, [Permission.DeleteCounterparty]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить контрагента "${currentCounterparty?.inn}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDeleteCounterparty();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = counterparties.find((c) => c.id === selectedRowKeys[0]);
            setCurrentCounterparty(entity);
        } else {
            setCurrentCounterparty(undefined);
        }
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 250 }}
                            key='searchText'
                            placeholder='ИНН / Название / Логин / ФИО '
                            value={filter?.searchText}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                        <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isArchived}
                                onChange={(value: any) => {
                                    d(setFilter({ ...filter, isArchived: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ICounterparty> = [
            {
                title: 'ИНН',
                dataIndex: 'inn',
                width: 160,
            },
            {
                title: 'Название',
                dataIndex: 'legalName',
                width: 250,
            },
            {
                title: 'Юр. адрес',
                width: 300,
                dataIndex: 'legalAddress',
            },
            {
                title: 'Основной',
                width: 90,
                align: 'center',
                render: (_: any, record) => {
                    return record.isMain ? <CheckCircleOutlined style={{ color: 'var(--main-green)' }} /> : '';
                },
            },
            {
                title: 'Логин',
                width: 130,
                render: (_, record) => {
                    return (
                        <a
                            href={`${window.location.protocol}//${window.location.host}/flc/settings/users/${record?.userId}`}
                            target='_blank'
                        >
                            {record.userLogin}
                        </a>
                    );
                },
            },
            {
                title: 'ФИО',
                dataIndex: 'userFullName',
                width: 300,
            },
            {
                title: 'Телефон',
                dataIndex: 'userPhone',
                width: 160,
            },
            {
                title: 'Email',
                dataIndex: 'userEmail',
                width: 300,
            },
            {},
        ];

        return (
            <Table
                virtual={true}
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={counterparties}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                    columnWidth: 32,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentCounterparty && counterpartyOpen && (
                <Counterparty
                    counterparty={currentCounterparty}
                    isViewOnly={!hasPermission(userSession.permissions, Permission.ManageCounterparties)}
                    onClose={(e) => {
                        setRefreshRequired(e);
                        setCounterpartyOpen(false);
                    }}
                    api={api}
                    serverFetch={serverFetch}
                />
            )}

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Counterparties;
